import { useCallback, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { fees, getProperty, isPropertyId, properties } from '../data/units'
import { BookingRequestPayload, BookingSubmissionResult, BookingType, GuestDetails, PropertyId } from '../types/booking'
import { estimatePrice, nightsBetween } from '../utils/pricing'
import { addHoursToTime } from '../utils/time'
import { BookingApiError, getIntegrationStatus, submitBookingRequest } from '../utils/bookingApi'

export type FieldErrors = Partial<Record<'dates' | 'shoot' | 'guests' | 'fullName' | 'email' | 'phone' | 'terms', string>>
type SubmitStatus = 'idle' | 'submitting' | 'redirecting' | 'done'

export const BOOKING_STEPS = ['Booking type', 'Details', 'Your information', 'Extras & review', 'Submit'] as const
export const TOTAL_STEPS = BOOKING_STEPS.length

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const phonePattern = /^\+?[\d\s()-]{7,20}$/

export function useBookingForm() {
  const [searchParams] = useSearchParams()
  const typeParam = searchParams.get('type')
  const propertyParam = searchParams.get('property')
  // /booking → stay (default, starts at step 1); /booking?type=photoshoot → photoshoot;
  // ?type=stay or ?property=APT… → stay, skipping straight to details.
  const presetType: BookingType | null =
    typeParam === 'photoshoot'
      ? 'photoshoot'
      : typeParam === 'stay' || typeParam === 'apartment' || isPropertyId(propertyParam)
        ? 'apartment'
        : null

  const [step, setStep] = useState(presetType ? 2 : 1)
  const [furthest, setFurthest] = useState(presetType ? 2 : 1)
  const [direction, setDirection] = useState<1 | -1>(1)

  const [bookingType, setBookingType] = useState<BookingType>(presetType ?? 'apartment')
  const [propertyId, setPropertyId] = useState<PropertyId>(isPropertyId(propertyParam) ? propertyParam : properties[0].id)
  const [checkIn, setCheckIn] = useState<string | null>(null)
  const [checkOut, setCheckOut] = useState<string | null>(null)
  const [guests, setGuests] = useState(2)
  const [shootDate, setShootDate] = useState<string | null>(null)
  const [startTime, setStartTime] = useState<string | null>(null)
  const [earlyCheckIn, setEarlyCheckIn] = useState(false)
  const [lateCheckout, setLateCheckout] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [guest, setGuest] = useState<GuestDetails>({ fullName: '', email: '', phone: '', specialRequests: '' })
  const [errors, setErrors] = useState<FieldErrors>({})

  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle')
  const inFlight = useRef(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [result, setResult] = useState<BookingSubmissionResult | null>(null)

  const integrationStatus = getIntegrationStatus()
  const property = getProperty(propertyId)
  const isStay = bookingType === 'apartment'
  const maxGuests = isStay ? property.maxGuests : fees.photoshootMaxGuests
  const endTime = startTime ? addHoursToTime(startTime, fees.photoshootHours) : null

  const estimate = useMemo(
    () => estimatePrice({ bookingType, propertyId, checkIn, checkOut, earlyCheckIn, lateCheckout }),
    [bookingType, propertyId, checkIn, checkOut, earlyCheckIn, lateCheckout],
  )

  const clearError = (key: keyof FieldErrors) => setErrors((current) => ({ ...current, [key]: undefined }))

  const changeType = useCallback((type: BookingType) => {
    setBookingType(type)
    setErrors({})
    const cap = type === 'apartment' ? getProperty(propertyId).maxGuests : fees.photoshootMaxGuests
    setGuests((current) => Math.min(current, cap))
    if (type === 'photoshoot') {
      setEarlyCheckIn(false)
      setLateCheckout(false)
    }
  }, [propertyId])

  const changeProperty = useCallback((id: PropertyId) => {
    setPropertyId(id)
    setGuests((current) => Math.min(current, getProperty(id).maxGuests))
  }, [])

  const changeDates = useCallback((start: string | null, end: string | null) => {
    setCheckIn(start)
    setCheckOut(end)
    clearError('dates')
  }, [])

  const changeShootDate = useCallback((date: string | null) => {
    setShootDate(date)
    clearError('shoot')
  }, [])

  const changeStartTime = useCallback((time: string) => {
    setStartTime(time)
    clearError('shoot')
  }, [])

  const changeGuests = useCallback((value: number) => {
    setGuests(value)
    clearError('guests')
  }, [])

  const updateGuest = useCallback((field: keyof GuestDetails, value: string) => {
    setGuest((current) => ({ ...current, [field]: value }))
    if (field !== 'specialRequests') clearError(field)
  }, [])

  const toggleTerms = useCallback(() => {
    setAcceptedTerms((v) => !v)
    clearError('terms')
  }, [])

  const validateStep = (target: number): FieldErrors => {
    const next: FieldErrors = {}
    if (target === 2) {
      if (isStay && (!checkIn || !checkOut || nightsBetween(checkIn, checkOut) < 1)) {
        next.dates = 'Choose a check-in date and a check-out date.'
      }
      if (!isStay && (!shootDate || !startTime)) next.shoot = 'Choose a shoot date and a start time.'
      if (guests < 1 || guests > maxGuests) next.guests = `Guests must be between 1 and ${maxGuests}.`
    }
    if (target === 3) {
      if (guest.fullName.trim().length < 2) next.fullName = 'Enter your full name.'
      if (!emailPattern.test(guest.email.trim())) next.email = 'Enter a valid email address.'
      if (!phonePattern.test(guest.phone.trim())) next.phone = 'Enter a valid phone number.'
    }
    if (target === 4 && !acceptedTerms) next.terms = 'Please confirm you accept the house rules and caution fee.'
    return next
  }

  const scrollToTop = () => {
    window.setTimeout(() => document.getElementById('booking-wizard')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30)
  }

  const next = () => {
    const stepErrors = validateStep(step)
    setErrors(stepErrors)
    if (Object.keys(stepErrors).length > 0) {
      window.setTimeout(() => {
        document.querySelector('#booking-wizard [role="alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 60)
      return
    }
    const target = Math.min(step + 1, TOTAL_STEPS)
    setDirection(1)
    setStep(target)
    setFurthest((f) => Math.max(f, target))
    scrollToTop()
  }

  const back = () => {
    setDirection(-1)
    setErrors({})
    setStep((s) => Math.max(1, s - 1))
    scrollToTop()
  }

  const goTo = (target: number) => {
    if (target > furthest || target === step || submitStatus === 'submitting') return
    setDirection(target > step ? 1 : -1)
    setErrors({})
    setStep(target)
    scrollToTop()
  }

  // The UI's 'apartment' type is sent to the backend as exactly 'stay'; never a display label.
  const payload: BookingRequestPayload = {
    bookingType: isStay ? 'stay' : 'photoshoot',
    fullName: guest.fullName.trim(),
    email: guest.email.trim(),
    phone: guest.phone.trim(),
    propertyId: isStay ? propertyId : '',
    checkIn: isStay ? (checkIn ?? '') : (shootDate ?? ''),
    checkOut: isStay ? (checkOut ?? '') : '',
    guests,
    earlyCheckIn: isStay && earlyCheckIn,
    lateCheckout: isStay && lateCheckout,
    specialRequests: guest.specialRequests.trim(),
    startTime: isStay ? '' : (startTime ?? ''),
    endTime: isStay ? '' : (endTime ?? ''),
  }

  const submit = async () => {
    if (inFlight.current || submitStatus !== 'idle' || integrationStatus !== 'ready') return
    const allErrors = { ...validateStep(2), ...validateStep(3), ...validateStep(4) }
    if (Object.keys(allErrors).length > 0) {
      setSubmitError('Some details are missing or invalid. Please review the earlier steps.')
      return
    }
    inFlight.current = true
    setSubmitError(null)
    setSubmitStatus('submitting')
    try {
      const submission = await submitBookingRequest(payload)
      const { ok, paymentUrl } = submission.response
      if (ok && paymentUrl) {
        // Hand over to the Paystack checkout URL returned by the backend. Payment is confirmed
        // only by server-side verification — never by the guest returning to this site.
        setSubmitStatus('redirecting')
        window.location.assign(paymentUrl)
        return
      }
      setResult(submission)
      setSubmitStatus('done')
    } catch (error) {
      setSubmitStatus('idle')
      setSubmitError(error instanceof BookingApiError ? error.message : 'Something went wrong. Nothing has been booked — please try again.')
    } finally {
      inFlight.current = false
    }
  }

  const editAfterRejection = () => {
    setResult(null)
    setSubmitStatus('idle')
    setDirection(-1)
    setStep(2)
    scrollToTop()
  }

  return {
    step,
    furthest,
    direction,
    next,
    back,
    goTo,
    bookingType,
    isStay,
    changeType,
    property,
    propertyId,
    changeProperty,
    checkIn,
    checkOut,
    changeDates,
    guests,
    changeGuests,
    maxGuests,
    shootDate,
    changeShootDate,
    startTime,
    endTime,
    changeStartTime,
    earlyCheckIn,
    lateCheckout,
    toggleEarly: () => setEarlyCheckIn((v) => !v),
    toggleLate: () => setLateCheckout((v) => !v),
    acceptedTerms,
    toggleTerms,
    guest,
    updateGuest,
    errors,
    estimate,
    payload,
    integrationStatus,
    submitStatus,
    submitError,
    result,
    submit,
    editAfterRejection,
  }
}

export type BookingForm = ReturnType<typeof useBookingForm>
